import { useFormContext } from 'react-hook-form';
import { Input, Select } from '@/components/common';
import type { FieldSchema } from '@/hooks/useInsuranceTypeSchema';
import { genericWidgetFor, GENERIC_YES_NO_OPTIONS, humanizeSection } from '../../genericSchema';

interface DynamicQuoteSectionProps {
  section: string;
  fields: FieldSchema[];
  description?: string;
}

/**
 * One schema section rendered as a wizard step body.
 *
 * The widget choice comes from `genericWidgetFor`, which delegates to the
 * Policy create form's `widgetForDataType` -- the two forms read the SAME
 * `InsuranceTypeFieldSchema` rows and must not disagree about what a `date` or
 * a `choice` looks like. What differs here is only the chrome: the wizard uses
 * the shared `Input`/`Select` components so a generated step is
 * indistinguishable from the hand-written motor/health ones, where
 * `components/policies/DynamicFieldSection.tsx` emits bare unstyled inputs.
 */
export function DynamicQuoteSection({ section, fields, description }: DynamicQuoteSectionProps) {
  const {
    register,
    formState: { errors },
  } = useFormContext();

  const sectionFields = fields.filter((f) => f.section === section);
  if (sectionFields.length === 0) return null;

  return (
    <div>
      <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
        {humanizeSection(section)}
      </h2>
      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 mb-6">
        {description ?? 'Fill in the details this insurance type needs for a quote.'}
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {sectionFields.map((field) => {
          const message = (errors as Record<string, { message?: string } | undefined>)[field.key]
            ?.message;
          const widget = genericWidgetFor(field);

          if (widget === 'select' || widget === 'yes-no') {
            const choices = widget === 'yes-no' ? GENERIC_YES_NO_OPTIONS : field.choices ?? [];
            return (
              <Select
                key={field.key}
                label={field.label}
                // `Select` renders its placeholder as a DISABLED option, so an
                // OPTIONAL field needs a real empty row or the user can never
                // un-answer it once they have picked something.
                options={field.is_required ? choices : [{ value: '', label: 'None' }, ...choices]}
                placeholder={
                  field.is_required ? `Select ${field.label.toLowerCase()}` : undefined
                }
                required={field.is_required}
                error={message}
                {...register(field.key)}
              />
            );
          }

          return (
            <Input
              key={field.key}
              label={field.label}
              // 'vehicle-search' can only appear on a motor-shaped schema, and
              // motor never routes through this flow. If a Country Admin points
              // a vehicle FK at another type anyway, a plain text box is the
              // honest fallback -- it stores what was typed rather than
              // silently dropping the field.
              type={widget === 'number' ? 'number' : widget === 'date' ? 'date' : 'text'}
              inputMode={widget === 'number' ? 'decimal' : undefined}
              required={field.is_required}
              error={message}
              {...register(field.key)}
            />
          );
        })}
      </div>
    </div>
  );
}

export default DynamicQuoteSection;
